// api.js — /api relative + Phase 0/1/2 methods
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = "/api";

class NetworkError extends Error {
    constructor(cause) {
        super("network");
        this.name = "NetworkError";
        this.isNetwork = true;
        this.cause = cause;
    }
}

/**
 * Wave B polish · build a human-readable error string from any backend
 * response body shape, including structured ``detail`` objects. Avoids
 * the ``[object Object]`` pitfall that happens when ``data.detail`` is
 * an object without a ``.message`` key and is handed straight to the
 * ``Error`` constructor (coerced via ``String()``).
 *
 * Priority order:
 *   1. detail.message / detail.error / detail.code
 *   2. top-level message
 *   3. JSON.stringify of the detail payload (bounded for safety)
 *   4. fallback sentinel
 */
export function _formatApiErrorMessage(data, fallback) {
    if (!data || typeof data !== "object") return fallback;
    const d = data.detail;
    if (typeof d === "string") return d;
    if (d && typeof d === "object") {
        if (typeof d.message === "string" && d.message) return d.message;
        if (typeof d.error === "string"   && d.error)   return d.error;
        if (typeof d.code === "string"    && d.code)    return d.code;
        try {
            const serialised = JSON.stringify(d);
            return serialised && serialised !== "{}"
                ? serialised.slice(0, 500)
                : fallback;
        } catch { /* fallthrough */ }
    }
    if (typeof data.message === "string" && data.message) return data.message;
    return fallback;
}

export async function apiFetch(path, options = {}) {
    let res;
    // Build headers/init AFTER spreading options so caller-supplied headers
    // merge with (not clobber) the Content-Type default.
    const { headers: callerHeaders, ...rest } = options;

    // Wave 2.4 · if an impersonated access token is stored, auto-inject
    // it as the Bearer header for every API call. This lets the sealed
    // auth path resolve the TARGET user identity while the Wave 2.4
    // middleware blocks sensitive paths with 403.
    let impersonationHeader = {};
    try {
        if (typeof localStorage !== "undefined") {
            const t = localStorage.getItem("impersonation_access_token");
            if (t) impersonationHeader = { Authorization: `Bearer ${t}` };
        }
    } catch { /* storage disabled · no-op */ }

    // Phase II · Wave D.1 · additive · inject the Wave-D-scoped locale
    // slug as X-Locale on every API call. Backend LocaleMiddleware
    // reads it and attaches request.state.locale (non-blocking).
    // Falls back silently when the Wave-D-scoped i18n is not available
    // (SSR / test environments without the config side-effect).
    let localeHeader = {};
    try {
        // eslint-disable-next-line global-require
        const { glmsI18n } = require("@/i18n/wave_d1/config");
        const lng = glmsI18n && glmsI18n.language;
        if (lng && typeof lng === "string") {
            localeHeader = { "X-Locale": lng };
        }
    } catch { /* glmsI18n not loaded · leave header absent */ }

    const init = {
        credentials: "include",
        ...rest,
        headers: {
            "Content-Type": "application/json",
            ...impersonationHeader,
            ...localeHeader,
            ...(callerHeaders || {}),
        },
    };
    try {
        res = await fetch(`${API}${path}`, init);
    } catch (netErr) {
        throw new NetworkError(netErr);
    }
    let data = null;
    const text = await res.text();
    if (text) {
        try { data = JSON.parse(text); } catch { data = { raw: text }; }
    }
    if (!res.ok) {
        // Phase 4: 423 Locked = user suspended. Redirect to a dedicated
        // page instead of surfacing a raw error toast.
        if (res.status === 423 && typeof window !== "undefined") {
            if (window.location.pathname !== "/account-suspended") {
                window.location.href = "/account-suspended";
            }
        }
        // F11 Phase A: 402 Payment Required = insufficient credits.
        // Fire a non-redirect toast with upgrade link so the caller can
        // still catch the error and render inline UX if desired.
        if (res.status === 402 && typeof window !== "undefined") {
            try {
                const detail = (data && data.detail) || {};
                const required = detail.required ?? "?";
                const available = detail.available ?? "?";
                const upgradeUrl = detail.upgrade_url || "/v2/subscription";
                // Lazy import to avoid eager coupling in SSR / tests.
                import("sonner").then(({ toast }) => {
                    toast.error(
                        `Credits insufficient (needed: ${required}, available: ${available})`,
                        {
                            description: "Upgrade plan or buy a credit pack.",
                            duration: 8000,
                            action: {
                                label: "Upgrade",
                                onClick: () => { window.location.href = upgradeUrl; },
                            },
                        },
                    );
                }).catch(() => {});
            } catch { /* swallow */ }
        }
        // Wave X.C.3 · 428 Precondition Required = admin must enroll 2FA.
        // Redirect to the account security page where TOTP enrollment lives.
        if (res.status === 428 && typeof window !== "undefined") {
            try {
                const detail = (data && data.detail) || {};
                if (detail.code === "2FA_ENROLLMENT_REQUIRED") {
                    const enrollUrl = detail.enroll_url || "/app/account/security";
                    import("sonner").then(({ toast }) => {
                        toast.error("2FA richiesto per gli admin", {
                            description: detail.message
                                || "Abilita 2FA per accedere agli endpoint sensibili.",
                            duration: 8000,
                            action: {
                                label: "Abilita 2FA",
                                onClick: () => {
                                    window.location.href = enrollUrl;
                                },
                            },
                        });
                    }).catch(() => {});
                    const onEnrollPage = window.location.pathname.startsWith(
                        "/app/account/security",
                    );
                    if (!onEnrollPage) {
                        window.location.href = enrollUrl;
                    }
                }
            } catch { /* swallow */ }
        }
        const err = new Error(
            _formatApiErrorMessage(data, `Request failed (${res.status})`),
        );
        err.status = res.status;
        err.data = data;
        throw err;
    }
    return data;
}

export const api = {
    // Auth / me
    me: () => apiFetch("/auth/me"),
    logout: () => apiFetch("/auth/logout", { method: "POST" }),
    postSession: (session_id) =>
        apiFetch("/auth/session", { method: "POST", body: JSON.stringify({ session_id }) }),
    mockLogin: (email, name) =>
        apiFetch("/auth/dev/mock-login", { method: "POST", body: JSON.stringify({ email, name }) }),

    // AI / credits
    askAi: (prompt) => apiFetch("/ai/test", { method: "POST", body: JSON.stringify({ prompt }) }),
    creditsHistory: () => apiFetch("/credits/history"),
    creditsUsageHour: () => apiFetch("/credits/usage-hour"),

    // Stripe
    createCheckout: (pkg = "starter_1000") =>
        apiFetch("/checkout/session", { method: "POST", body: JSON.stringify({ package: pkg }) }),
    verifyCheckout: (session_id) =>
        apiFetch(`/checkout/verify?session_id=${encodeURIComponent(session_id)}`),

    // Lead Engine
    leadsSearch: (icp, batch_size) =>
        apiFetch("/leads/search", { method: "POST", body: JSON.stringify({ icp, batch_size }) }),
    leadsSearches: (limit = 20, offset = 0) =>
        apiFetch(`/leads/searches?limit=${limit}&offset=${offset}`),
    leadsSearchDetail: (id) => apiFetch(`/leads/searches/${encodeURIComponent(id)}`),
    leadsSearchDelete: (id) =>
        apiFetch(`/leads/searches/${encodeURIComponent(id)}`, { method: "DELETE" }),
    leadsPatch: (leadId, patch) =>
        apiFetch(`/leads/${encodeURIComponent(leadId)}`, { method: "PATCH", body: JSON.stringify(patch) }),
    leadsSaved: (limit = 100, offset = 0) =>
        apiFetch(`/leads/saved?limit=${limit}&offset=${offset}`),
    leadsExportCsvUrl: (id) => `${API}/leads/searches/${encodeURIComponent(id)}/export.csv`,

    // Outreach
    outreachMeta: () => apiFetch("/outreach/meta"),
    outreachAiCompose: (body) =>
        apiFetch("/outreach/ai-compose", { method: "POST", body: JSON.stringify(body) }),

    // templates
    outreachTemplates: () => apiFetch("/outreach/templates"),
    outreachTemplateCreate: (body) =>
        apiFetch("/outreach/templates", { method: "POST", body: JSON.stringify(body) }),
    outreachTemplatePatch: (id, patch) =>
        apiFetch(`/outreach/templates/${encodeURIComponent(id)}`, {
            method: "PATCH", body: JSON.stringify(patch),
        }),
    outreachTemplateDelete: (id) =>
        apiFetch(`/outreach/templates/${encodeURIComponent(id)}`, { method: "DELETE" }),

    // sequences
    outreachSequences: () => apiFetch("/outreach/sequences"),
    outreachSequenceCreate: (body) =>
        apiFetch("/outreach/sequences", { method: "POST", body: JSON.stringify(body) }),
    outreachSequencePatch: (id, patch) =>
        apiFetch(`/outreach/sequences/${encodeURIComponent(id)}`, {
            method: "PATCH", body: JSON.stringify(patch),
        }),
    outreachSequenceDelete: (id) =>
        apiFetch(`/outreach/sequences/${encodeURIComponent(id)}`, { method: "DELETE" }),

    // campaigns
    outreachCampaigns: (limit = 50, offset = 0) =>
        apiFetch(`/outreach/campaigns?limit=${limit}&offset=${offset}`),
    outreachCampaign: (id) =>
        apiFetch(`/outreach/campaigns/${encodeURIComponent(id)}`),
    outreachCampaignCreate: (body) =>
        apiFetch("/outreach/campaigns", { method: "POST", body: JSON.stringify(body) }),
    outreachCampaignPatch: (id, patch) =>
        apiFetch(`/outreach/campaigns/${encodeURIComponent(id)}`, {
            method: "PATCH", body: JSON.stringify(patch),
        }),
    outreachCampaignDelete: (id) =>
        apiFetch(`/outreach/campaigns/${encodeURIComponent(id)}`, { method: "DELETE" }),
    outreachCampaignLaunch: (id) =>
        apiFetch(`/outreach/campaigns/${encodeURIComponent(id)}/launch`, { method: "POST" }),
    outreachCampaignPause: (id) =>
        apiFetch(`/outreach/campaigns/${encodeURIComponent(id)}/pause`, { method: "POST" }),
    outreachCampaignResume: (id) =>
        apiFetch(`/outreach/campaigns/${encodeURIComponent(id)}/resume`, { method: "POST" }),
    outreachCampaignStop: (id) =>
        apiFetch(`/outreach/campaigns/${encodeURIComponent(id)}/stop`, { method: "POST" }),
    outreachRecipients: (cid, opts = {}) => {
        const q = new URLSearchParams();
        if (opts.status) q.set("status", opts.status);
        q.set("limit", opts.limit ?? 100);
        q.set("offset", opts.offset ?? 0);
        return apiFetch(`/outreach/campaigns/${encodeURIComponent(cid)}/recipients?${q}`);
    },
    outreachRecipientsImport: (cid, body) =>
        apiFetch(`/outreach/campaigns/${encodeURIComponent(cid)}/recipients`, {
            method: "POST", body: JSON.stringify(body),
        }),
    outreachRecipientPatch: (cid, rid, body) =>
        apiFetch(`/outreach/campaigns/${encodeURIComponent(cid)}/recipients/${encodeURIComponent(rid)}`, {
            method: "PATCH", body: JSON.stringify(body),
        }),

    // Unsubscribe (public)
    outreachUnsubInfo: (token) =>
        apiFetch(`/outreach/unsubscribe/${encodeURIComponent(token)}`),
    outreachUnsubConfirm: (token, reason) =>
        apiFetch(`/outreach/unsubscribe/${encodeURIComponent(token)}`, {
            method: "POST",
            body: JSON.stringify({ reason: reason || null }),
        }),

    // ---------- Phase 3 · Plans / Billing ----------
    plansPublic: () => apiFetch("/plans"),
    planMe: () => apiFetch("/plans/me"),
    subscriptionCheckout: (plan_id, interval = "monthly") =>
        apiFetch("/billing/subscription-checkout", {
            method: "POST",
            body: JSON.stringify({ plan_id, interval }),
        }),
    billingPortal: () => apiFetch("/billing/portal-session", { method: "POST" }),
    billingStatus: () => apiFetch("/billing/status"),
    verifySubscription: (session_id) =>
        apiFetch(`/billing/verify?session_id=${encodeURIComponent(session_id)}`),

    // ---------- Phase 3 · Affiliate ----------
    affiliateMe: () => apiFetch("/affiliate/me"),
    affiliateStats: () => apiFetch("/affiliate/stats"),
    affiliateTrackClick: (payload) =>
        apiFetch("/affiliate/click", {
            method: "POST",
            body: JSON.stringify(payload),
        }),

    // ---------- Phase 3 · Enterprise ----------
    enterpriseContact: (payload) =>
        apiFetch("/enterprise/contact", {
            method: "POST",
            body: JSON.stringify(payload),
        }),

    // ---------- Phase 3 · Admin ----------
    adminEnterpriseLeads: (status) =>
        apiFetch(
            `/admin/enterprise-leads${status ? `?status=${encodeURIComponent(status)}` : ""}`,
        ),
    adminPatchEnterpriseLead: (id, patch) =>
        apiFetch(`/admin/enterprise-leads/${encodeURIComponent(id)}`, {
            method: "PATCH",
            body: JSON.stringify(patch),
        }),
    adminAffiliatePayouts: () => apiFetch("/admin/affiliate-payouts"),
    adminMarkPayoutPaid: (payload) =>
        apiFetch("/admin/affiliate-payouts/mark-paid", {
            method: "POST",
            body: JSON.stringify(payload),
        }),

    // ---------- Phase 4 · Universe ----------
    universeGraph: (force = false) =>
        apiFetch(`/universe/graph${force ? "?force=true" : ""}`),
    universeWorlds: () => apiFetch("/universe/worlds"),
    universeWorldCreate: (body) =>
        apiFetch("/universe/worlds", { method: "POST", body: JSON.stringify(body) }),
    universeWorldUpdate: (id, body) =>
        apiFetch(`/universe/worlds/${encodeURIComponent(id)}`, {
            method: "PATCH",
            body: JSON.stringify(body),
        }),
    universeWorldDelete: (id) =>
        apiFetch(`/universe/worlds/${encodeURIComponent(id)}`, { method: "DELETE" }),
    universeWorldAddEntity: (wid, body) =>
        apiFetch(`/universe/worlds/${encodeURIComponent(wid)}/entities`, {
            method: "POST",
            body: JSON.stringify(body),
        }),

    // ---------- Phase 4 · Civilization ----------
    civilizationMe: () => apiFetch("/civilization/me"),
    civilizationLeaderboard: (scope = "era", limit = 100) =>
        apiFetch(`/civilization/leaderboard?scope=${scope}&limit=${limit}`),
    civilizationPublicProfile: (userId) =>
        apiFetch(`/civilization/profile/${encodeURIComponent(userId)}`),
    civilizationBadges: () => apiFetch("/civilization/badges"),
    civilizationMyBadges: () => apiFetch("/civilization/badges/mine"),
    civilizationCurrentEra: () => apiFetch("/civilization/eras/current"),
    civilizationSettings: (body) =>
        apiFetch("/civilization/settings", { method: "PATCH", body: JSON.stringify(body) }),
    civilizationAdminAdjust: (body) =>
        apiFetch("/civilization/admin/adjust", { method: "POST", body: JSON.stringify(body) }),
    civilizationAdminEraTick: () =>
        apiFetch("/civilization/admin/eras/tick", { method: "POST" }),

    // ---------- Phase 4 · Economy ----------
    economyPricing: (action) =>
        apiFetch(`/economy/pricing?action=${encodeURIComponent(action)}`),
    economyMe: () => apiFetch("/economy/me"),
    adminEconomyDashboard: () => apiFetch("/admin/economy/dashboard"),
    adminEconomyRecompute: () =>
        apiFetch("/admin/economy/recompute-now", { method: "POST" }),
    adminEconomyUnsuspend: (body) =>
        apiFetch("/admin/economy/unsuspend", { method: "POST", body: JSON.stringify(body) }),

    // ---------- Phase 5 · Module Builder ----------
    modulesBrowse: (opts = {}) => {
        const q = new URLSearchParams();
        if (opts.category) q.set("category", opts.category);
        if (opts.min_plan) q.set("min_plan", opts.min_plan);
        if (opts.q) q.set("q", opts.q);
        const s = q.toString();
        return apiFetch(`/modules/browse${s ? `?${s}` : ""}`);
    },
    moduleDetail: (slug) => apiFetch(`/modules/${encodeURIComponent(slug)}`),
    moduleRun: (slug, inputs, idempotencyKey) => {
        const headers = {};
        if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey;
        return apiFetch(`/modules/${encodeURIComponent(slug)}/run`, {
            method: "POST",
            headers,
            body: JSON.stringify({ inputs }),
        });
    },
    moduleMyRuns: (moduleId) =>
        apiFetch(`/modules/my/runs${moduleId ? `?module_id=${encodeURIComponent(moduleId)}` : ""}`),

    // Admin
    adminModulesList: (opts = {}) => {
        const q = new URLSearchParams();
        if (opts.status) q.set("status", opts.status);
        if (opts.category) q.set("category", opts.category);
        const s = q.toString();
        return apiFetch(`/admin/modules${s ? `?${s}` : ""}`);
    },
    adminModuleCreate: (body) =>
        apiFetch("/admin/modules", { method: "POST", body: JSON.stringify(body) }),
    adminModulePatch: (id, patch) =>
        apiFetch(`/admin/modules/${encodeURIComponent(id)}`, {
            method: "PATCH",
            body: JSON.stringify(patch),
        }),
    adminModulePublish: (id) =>
        apiFetch(`/admin/modules/${encodeURIComponent(id)}/publish`, { method: "POST" }),
    adminModuleArchive: (id) =>
        apiFetch(`/admin/modules/${encodeURIComponent(id)}/archive`, { method: "POST" }),
    adminModuleDelete: (id) =>
        apiFetch(`/admin/modules/${encodeURIComponent(id)}`, { method: "DELETE" }),
    adminModuleTest: (id, inputs) =>
        apiFetch(`/admin/modules/${encodeURIComponent(id)}/test`, {
            method: "POST",
            body: JSON.stringify({ inputs }),
        }),
    adminModuleSuggest: (context) =>
        apiFetch("/admin/modules/suggest", {
            method: "POST",
            body: JSON.stringify({ context }),
        }),
    adminModuleSuggestApply: (moduleSchema) =>
        apiFetch("/admin/modules/suggest/apply", {
            method: "POST",
            body: JSON.stringify({ module_schema: moduleSchema }),
        }),

    // ---------- Phase 5 · Marketplace ----------
    marketplaceAssets: (opts = {}) => {
        const q = new URLSearchParams();
        Object.entries(opts).forEach(([k, v]) => v !== undefined && q.set(k, v));
        const s = q.toString();
        return apiFetch(`/marketplace/assets${s ? `?${s}` : ""}`);
    },
    marketplaceAsset: (slug) => apiFetch(`/marketplace/assets/${encodeURIComponent(slug)}`),
    marketplaceBuy: (assetId) =>
        apiFetch(`/marketplace/purchase/${encodeURIComponent(assetId)}`, { method: "POST" }),
    marketplaceLibrary: () => apiFetch("/marketplace/library"),
    marketplaceReapply: (purchaseId) =>
        apiFetch(`/marketplace/library/${encodeURIComponent(purchaseId)}/reapply`, {
            method: "POST",
        }),
    marketplaceReviews: (assetId, limit = 50) =>
        apiFetch(`/marketplace/assets/${encodeURIComponent(assetId)}/reviews?limit=${limit}`),
    marketplaceReviewCreate: (assetId, rating, comment) =>
        apiFetch(`/marketplace/assets/${encodeURIComponent(assetId)}/reviews`, {
            method: "POST",
            body: JSON.stringify({ rating, comment }),
        }),

    // Admin marketplace
    adminMarketplaceList: () => apiFetch("/admin/marketplace/assets"),
    adminMarketplaceCreate: (body) =>
        apiFetch("/admin/marketplace/assets", { method: "POST", body: JSON.stringify(body) }),
    adminMarketplacePatch: (id, patch) =>
        apiFetch(`/admin/marketplace/assets/${encodeURIComponent(id)}`, {
            method: "PATCH",
            body: JSON.stringify(patch),
        }),
    adminMarketplacePublish: (id) =>
        apiFetch(`/admin/marketplace/assets/${encodeURIComponent(id)}/publish`, {
            method: "POST",
        }),
    adminMarketplaceArchive: (id) =>
        apiFetch(`/admin/marketplace/assets/${encodeURIComponent(id)}/archive`, {
            method: "POST",
        }),
    adminMarketplaceToggleFeature: (id) =>
        apiFetch(`/admin/marketplace/assets/${encodeURIComponent(id)}/feature`, {
            method: "POST",
        }),
    adminMarketplaceDelete: (id) =>
        apiFetch(`/admin/marketplace/assets/${encodeURIComponent(id)}`, { method: "DELETE" }),

    // ---------- Phase 6A · Blog (public) ----------
    blogPosts: (opts = {}) => {
        const q = new URLSearchParams();
        Object.entries(opts).forEach(([k, v]) => v !== undefined && v !== "" && q.set(k, v));
        const s = q.toString();
        return apiFetch(`/blog/posts${s ? `?${s}` : ""}`);
    },
    blogPost: (slug) => apiFetch(`/blog/posts/${encodeURIComponent(slug)}`),
    blogCategories: () => apiFetch("/blog/categories"),

    // ---------- Phase 6A · Blog admin ----------
    adminBlogList: (opts = {}) => {
        const q = new URLSearchParams();
        Object.entries(opts).forEach(([k, v]) => v !== undefined && v !== "" && q.set(k, v));
        const s = q.toString();
        return apiFetch(`/admin/blog/posts${s ? `?${s}` : ""}`);
    },
    adminBlogGet: (id) => apiFetch(`/admin/blog/posts/${encodeURIComponent(id)}`),
    adminBlogCreate: (body) =>
        apiFetch("/admin/blog/posts", { method: "POST", body: JSON.stringify(body) }),
    adminBlogPatch: (id, patch) =>
        apiFetch(`/admin/blog/posts/${encodeURIComponent(id)}`, {
            method: "PATCH",
            body: JSON.stringify(patch),
        }),
    adminBlogPublish: (id) =>
        apiFetch(`/admin/blog/posts/${encodeURIComponent(id)}/publish`, { method: "POST" }),
    adminBlogUnpublish: (id) =>
        apiFetch(`/admin/blog/posts/${encodeURIComponent(id)}/unpublish`, { method: "POST" }),
    adminBlogDelete: (id) =>
        apiFetch(`/admin/blog/posts/${encodeURIComponent(id)}`, { method: "DELETE" }),
    adminBlogAiDraft: (topic, category_slug, locale = "en") =>
        apiFetch("/admin/blog/ai-draft", {
            method: "POST",
            body: JSON.stringify({ topic, category_slug, locale }),
        }),

    // ---------- Phase 6A · Admin Dashboard KPI ----------
    adminDashboardKpi: () => apiFetch("/admin/dashboard/kpi"),

    // ---------- Phase 6A · Username resolve ----------
    seoResolveUsername: (username) =>
        apiFetch(`/seo/username/${encodeURIComponent(username)}`),

    // ---------- Phase 9 · Fraud + Governance + Content Mesh + FAQ + ETF ----------
    adminFraudScan: (dry_run = false) =>
        apiFetch("/admin/fraud/scan", {
            method: "POST", body: JSON.stringify({ dry_run }),
        }),
    adminFraudScanStatus: () => apiFetch("/admin/fraud/scan-status"),
    adminFraudAlerts: (opts = {}) => {
        const q = new URLSearchParams();
        Object.entries(opts).forEach(([k, v]) => (v || v === 0) && q.set(k, v));
        const s = q.toString();
        return apiFetch(`/admin/fraud/alerts${s ? `?${s}` : ""}`);
    },
    adminGovernanceDashboard: () => apiFetch("/admin/governance/dashboard"),
    adminGovernanceRollback: (auditLogId, reason = "") =>
        apiFetch(`/admin/governance/rollback/${encodeURIComponent(auditLogId)}`, {
            method: "POST", body: JSON.stringify({ reason }),
        }),
    adminContentMeshRun: (dry_run = false) =>
        apiFetch("/admin/content-mesh/run-now", {
            method: "POST", body: JSON.stringify({ dry_run }),
        }),
    adminContentMeshLastRuns: (limit = 10) =>
        apiFetch(`/admin/content-mesh/last-runs?limit=${limit}`),
    adminFaqRun: (dry_run = false) =>
        apiFetch("/admin/faq/run-now", {
            method: "POST", body: JSON.stringify({ dry_run }),
        }),
    adminFaqSuggestions: (status = "pending") =>
        apiFetch(`/admin/faq/suggestions?status=${encodeURIComponent(status)}`),
    adminFaqApprove: (id, edits = null) =>
        apiFetch(`/admin/faq/${encodeURIComponent(id)}/approve`, {
            method: "POST",
            body: JSON.stringify(edits ? { edits } : {}),
        }),
    adminFaqReject: (id, reason = "") =>
        apiFetch(`/admin/faq/${encodeURIComponent(id)}/reject`, {
            method: "POST", body: JSON.stringify({ reason }),
        }),
    adminEtfRun: (dry_run = false) =>
        apiFetch("/admin/etf/run-now", {
            method: "POST", body: JSON.stringify({ dry_run }),
        }),
    adminEtfDrafts: (limit = 50) =>
        apiFetch(`/admin/etf/drafts?limit=${limit}`),
    adminEtfLastRuns: (limit = 10) =>
        apiFetch(`/admin/etf/last-runs?limit=${limit}`),

    // ---------- Phase 10 · V30 (Mini-Landing + Optimize + Growth + Recycle + Schedule) ----------
    // Mini-Landings (admin)
    adminMiniLandingTemplates: () => apiFetch("/admin/mini-landings/templates"),
    adminMiniLandingsList: (params = {}) => {
        const q = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => (v || v === 0) && q.set(k, v));
        const s = q.toString();
        return apiFetch(`/admin/mini-landings${s ? `?${s}` : ""}`);
    },
    adminMiniLandingGet: (id) => apiFetch(`/admin/mini-landings/${encodeURIComponent(id)}`),
    adminMiniLandingCreate: (payload) =>
        apiFetch("/admin/mini-landings", {
            method: "POST", body: JSON.stringify(payload),
        }),
    adminMiniLandingUpdate: (id, edits) =>
        apiFetch(`/admin/mini-landings/${encodeURIComponent(id)}`, {
            method: "PATCH", body: JSON.stringify(edits),
        }),
    adminMiniLandingPublish: (id) =>
        apiFetch(`/admin/mini-landings/${encodeURIComponent(id)}/publish`, {
            method: "POST", body: JSON.stringify({}),
        }),
    adminMiniLandingArchive: (id, reason = "") =>
        apiFetch(`/admin/mini-landings/${encodeURIComponent(id)}/archive`, {
            method: "POST", body: JSON.stringify({ reason }),
        }),
    adminMiniLandingAiDraft: (payload) =>
        apiFetch("/admin/mini-landings/ai-draft", {
            method: "POST", body: JSON.stringify(payload),
        }),
    // Public mini-landing
    miniLandingGetPublic: (slug, preview = false) =>
        apiFetch(`/mini-landings/${encodeURIComponent(slug)}${preview ? "?preview=true" : ""}`),
    miniLandingCtaClick: (slug) =>
        apiFetch(`/mini-landings/${encodeURIComponent(slug)}/cta-click`, {
            method: "POST", body: JSON.stringify({}),
        }),

    // Optimize
    v30Optimize: (payload) =>
        apiFetch("/v30/optimize", { method: "POST", body: JSON.stringify(payload) }),
    v30OptimizeJob: (jobId) => apiFetch(`/v30/optimize/${encodeURIComponent(jobId)}`),
    v30OptimizePending: (limit = 50) => apiFetch(`/v30/optimize/pending?limit=${limit}`),
    v30OptimizeApprove: (variantId, apply_to_source = false) =>
        apiFetch(`/v30/optimize/variants/${encodeURIComponent(variantId)}/approve`, {
            method: "POST", body: JSON.stringify({ apply_to_source }),
        }),
    v30OptimizeReject: (variantId, reason = "") =>
        apiFetch(`/v30/optimize/variants/${encodeURIComponent(variantId)}/reject`, {
            method: "POST", body: JSON.stringify({ reason }),
        }),

    // Growth
    v30Growth: (payload = { scope: "all", dry_run: false }) =>
        apiFetch("/v30/growth", { method: "POST", body: JSON.stringify(payload) }),
    v30GrowthActions: (params = {}) => {
        const q = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => (v || v === 0) && q.set(k, v));
        const s = q.toString();
        return apiFetch(`/v30/growth/actions${s ? `?${s}` : ""}`);
    },
    v30GrowthApprove: (id) =>
        apiFetch(`/v30/growth/actions/${encodeURIComponent(id)}/approve`, {
            method: "POST", body: JSON.stringify({}),
        }),
    v30GrowthReject: (id, reason = "") =>
        apiFetch(`/v30/growth/actions/${encodeURIComponent(id)}/reject`, {
            method: "POST", body: JSON.stringify({ reason }),
        }),

    // Recycle
    v30Recycle: (payload) =>
        apiFetch("/v30/recycle", { method: "POST", body: JSON.stringify(payload) }),
    v30RecycleJob: (jobId) => apiFetch(`/v30/recycle/${encodeURIComponent(jobId)}`),
    v30RecyclePending: (limit = 50) => apiFetch(`/v30/recycle/pending?limit=${limit}`),
    v30RecycleApprove: (variantId) =>
        apiFetch(`/v30/recycle/variants/${encodeURIComponent(variantId)}/approve`, {
            method: "POST", body: JSON.stringify({}),
        }),
    v30RecycleReject: (variantId, reason = "") =>
        apiFetch(`/v30/recycle/variants/${encodeURIComponent(variantId)}/reject`, {
            method: "POST", body: JSON.stringify({ reason }),
        }),

    // Schedule
    v30ScheduleList: (params = {}) => {
        const q = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => (v || v === 0) && q.set(k, v));
        const s = q.toString();
        return apiFetch(`/v30/schedule${s ? `?${s}` : ""}`);
    },
    v30ScheduleCreate: (payload) =>
        apiFetch("/v30/schedule", { method: "POST", body: JSON.stringify(payload) }),
    v30ScheduleUpdate: (id, edits) =>
        apiFetch(`/v30/schedule/${encodeURIComponent(id)}`, {
            method: "PATCH", body: JSON.stringify(edits),
        }),
    v30ScheduleMarkPublished: (id, external_url = "") =>
        apiFetch(`/v30/schedule/${encodeURIComponent(id)}/mark-published`, {
            method: "POST", body: JSON.stringify({ external_url }),
        }),
    v30ScheduleDelete: (id) =>
        apiFetch(`/v30/schedule/${encodeURIComponent(id)}`, { method: "DELETE" }),

    // ---------- Phase 11 · V50 Self-Optimization ----------
    v50Run: () => apiFetch("/v50/run", { method: "POST", body: JSON.stringify({}) }),
    v50Reports: (limit = 20) => apiFetch(`/v50/reports?limit=${limit}`),
    v50Report: (id) => apiFetch(`/v50/reports/${encodeURIComponent(id)}`),
    v50Acknowledge: (reportId, suggestionId) =>
        apiFetch(
            `/v50/reports/${encodeURIComponent(reportId)}/acknowledge/${encodeURIComponent(suggestionId)}`,
            { method: "POST", body: JSON.stringify({}) },
        ),
    v50Dismiss: (reportId, suggestionId, reason = "") =>
        apiFetch(
            `/v50/reports/${encodeURIComponent(reportId)}/dismiss/${encodeURIComponent(suggestionId)}`,
            { method: "POST", body: JSON.stringify({ reason }) },
        ),
    v50PendingSuggestions: (limit = 200) =>
        apiFetch(`/v50/suggestions/pending?limit=${limit}`),

    // ---------- Phase 12 · Email Engine V10 ----------
    emailEngineRun: (dry_run = false) => apiFetch("/email-engine/run", {
        method: "POST", body: JSON.stringify({ dry_run }),
    }),
    emailEngineReport: (id) => apiFetch(`/email-engine/run/${encodeURIComponent(id)}`),
    emailEngineReports: (limit = 10) => apiFetch(`/email-engine/reports?limit=${limit}`),
    emailEngineTemplates: (category) => apiFetch(
        `/email-engine/templates${category ? `?category=${encodeURIComponent(category)}` : ""}`),
    emailEnginePreview: (templateId, userId) => apiFetch(
        `/email-engine/templates/${encodeURIComponent(templateId)}/preview?user_id=${encodeURIComponent(userId)}`),
    emailEngineSegments: () => apiFetch("/email-engine/segments"),
    emailEngineTotals: () => apiFetch("/email-engine/totals"),
    emailEnginePending: (limit = 200) => apiFetch(`/email-engine/suggestions/pending?limit=${limit}`),
    emailEngineAcknowledge: (id) => apiFetch(
        `/email-engine/acknowledge/${encodeURIComponent(id)}`,
        { method: "POST", body: JSON.stringify({}) }),
    emailEngineDismiss: (id, reason = "") => apiFetch(
        `/email-engine/dismiss/${encodeURIComponent(id)}`,
        { method: "POST", body: JSON.stringify({ reason }) }),

    // ---------- Phase 13 · SEO Engine V10 ----------
    seoEngineRun: (dry_run = false) => apiFetch("/seo-engine/run", {
        method: "POST", body: JSON.stringify({ dry_run }),
    }),
    seoEngineReport: (id) => apiFetch(`/seo-engine/run/${encodeURIComponent(id)}`),
    seoEngineReports: (limit = 10) => apiFetch(`/seo-engine/reports?limit=${limit}`),
    seoEngineClusters: (status) => apiFetch(
        `/seo-engine/clusters${status ? `?status=${encodeURIComponent(status)}` : ""}`),
    seoEngineTopics: (priority) => apiFetch(
        `/seo-engine/topics${priority ? `?priority=${encodeURIComponent(priority)}` : ""}`),
    seoEngineTotals: () => apiFetch("/seo-engine/totals"),
    seoEnginePending: (limit = 200) => apiFetch(`/seo-engine/suggestions/pending?limit=${limit}`),
    seoEngineAcknowledge: (id) => apiFetch(
        `/seo-engine/acknowledge/${encodeURIComponent(id)}`,
        { method: "POST", body: JSON.stringify({}) }),
    seoEngineDismiss: (id, reason = "") => apiFetch(
        `/seo-engine/dismiss/${encodeURIComponent(id)}`,
        { method: "POST", body: JSON.stringify({ reason }) }),

    // ---------- Phase 14 · SEO Apply Layer ----------
    seoApplyTotals: () => apiFetch("/seo-apply/totals"),
    seoApplyWhitelist: () => apiFetch("/seo-apply/whitelist"),
    seoApplyAcknowledged: (limit = 100) =>
        apiFetch(`/seo-apply/acknowledged?limit=${limit}`),
    seoApplyLogs: (opts = {}) => {
        const q = new URLSearchParams();
        Object.entries(opts).forEach(([k, v]) => (v || v === 0) && q.set(k, v));
        const s = q.toString();
        return apiFetch(`/seo-apply/logs${s ? `?${s}` : ""}`);
    },
    seoApplyLog: (applyId) =>
        apiFetch(`/seo-apply/logs/${encodeURIComponent(applyId)}`),
    seoApplyApply: (suggestionId) =>
        apiFetch(`/seo-apply/apply/${encodeURIComponent(suggestionId)}`, {
            method: "POST", body: JSON.stringify({}),
        }),
    seoApplyRollback: (applyId) =>
        apiFetch(`/seo-apply/rollback/${encodeURIComponent(applyId)}`, {
            method: "POST", body: JSON.stringify({}),
        }),
    seoApplyWeeklyReports: (limit = 8) =>
        apiFetch(`/seo-apply/weekly-reports?limit=${limit}`),
    seoApplyToggleFlag: (key, value, reason = "") =>
        apiFetch(`/admin/system-settings/${encodeURIComponent(key)}`, {
            method: "PATCH",
            body: JSON.stringify({ value, reason }),
        }),

    // ---------- Phase 15 · Email Send Layer ----------
    emailSendStatus: () => apiFetch("/email-send/status"),
    emailSendTotals: () => apiFetch("/email-send/totals"),
    emailSendApplicable: (limit = 100) =>
        apiFetch(`/email-send/applicable?limit=${limit}`),
    emailSendPreview: (suggestionId, userId, email) =>
        apiFetch(`/email-send/preview/${encodeURIComponent(suggestionId)}`, {
            method: "POST",
            body: JSON.stringify({ user_id: userId, email }),
        }),
    emailSendDispatch: (suggestionId) =>
        apiFetch(`/email-send/dispatch/${encodeURIComponent(suggestionId)}`, {
            method: "POST", body: JSON.stringify({}),
        }),
    emailSendSchedule: (suggestionId, scheduled_at) =>
        apiFetch(`/email-send/schedule/${encodeURIComponent(suggestionId)}`, {
            method: "POST", body: JSON.stringify({ scheduled_at }),
        }),
    emailSendDispatchDue: (scheduledId) =>
        apiFetch(`/email-send/dispatch-due/${encodeURIComponent(scheduledId)}`, {
            method: "POST", body: JSON.stringify({}),
        }),
    emailSendCancel: (scheduledId) =>
        apiFetch(`/email-send/cancel/${encodeURIComponent(scheduledId)}`, {
            method: "POST", body: JSON.stringify({}),
        }),
    emailSendRetract: (logId, reason) =>
        apiFetch(`/email-send/retract/${encodeURIComponent(logId)}`, {
            method: "POST", body: JSON.stringify({ reason }),
        }),
    emailSendLogs: (opts = {}) => {
        const q = new URLSearchParams();
        Object.entries(opts).forEach(([k, v]) => (v || v === 0) && q.set(k, v));
        const s = q.toString();
        return apiFetch(`/email-send/logs${s ? `?${s}` : ""}`);
    },
    emailSendLog: (logId) =>
        apiFetch(`/email-send/logs/${encodeURIComponent(logId)}`),
    emailSendForceTick: () =>
        apiFetch("/email-send/cron/force-tick", { method: "POST" }),
    emailSendWeeklyReports: (limit = 8) =>
        apiFetch(`/email-send/weekly-reports?limit=${limit}`),

    // ---------- Phase 16a · Rollout Gate + V50 Digest ----------
    emailSendRolloutStatus: () => apiFetch("/email-send/rollout/status"),
    emailSendRolloutAdvance: (target_step, reason) =>
        apiFetch("/email-send/rollout/advance", {
            method: "POST",
            body: JSON.stringify({ target_step, reason }),
        }),
    v50DigestGenerateNow: () =>
        apiFetch("/v50/digest/generate-now", {
            method: "POST", body: JSON.stringify({}),
        }),
    v50DigestList: (limit = 10) =>
        apiFetch(`/v50/digests?limit=${limit}`),

    // ---------- Phase 16b · Affiliate v2 ----------
    // Self
    affV2Dashboard: () => apiFetch("/affiliate/v2/dashboard"),
    affV2CreateLink: (landing_url, campaign_name = null) =>
        apiFetch("/affiliate/v2/links", {
            method: "POST",
            body: JSON.stringify({ landing_url, campaign_name }),
        }),
    affV2ListLinks: (limit = 100) =>
        apiFetch(`/affiliate/v2/links?limit=${limit}`),
    affV2ListClicks: (limit = 100) =>
        apiFetch(`/affiliate/v2/clicks?limit=${limit}`),
    affV2ListConversions: (limit = 50) =>
        apiFetch(`/affiliate/v2/conversions?limit=${limit}`),
    affV2SimulateConversion: (body) =>
        apiFetch("/affiliate/v2/simulate-conversion", {
            method: "POST", body: JSON.stringify(body),
        }),
    // Admin
    affV2AdminOverview: () => apiFetch("/admin/affiliate/v2/overview"),
    affV2AdminAffiliates: (limit = 50) =>
        apiFetch(`/admin/affiliate/v2/affiliates?limit=${limit}`),
    affV2AdminFraudFlags: (resolved = false, limit = 100) =>
        apiFetch(`/admin/affiliate/v2/fraud-flags?resolved=${resolved}&limit=${limit}`),
    affV2AdminResolveFlag: (flagId, action, notes = "") =>
        apiFetch(`/admin/affiliate/v2/fraud-flags/${encodeURIComponent(flagId)}/resolve`, {
            method: "POST", body: JSON.stringify({ action, notes }),
        }),
    affV2AdminScanNow: (dry_run = false) =>
        apiFetch(`/admin/affiliate/v2/fraud-flags/scan-now?dry_run=${dry_run}`, {
            method: "POST", body: "{}",
        }),
    affV2AdminPayouts: (opts = {}) => {
        const q = new URLSearchParams();
        Object.entries(opts).forEach(([k, v]) => (v || v === 0) && q.set(k, v));
        return apiFetch(`/admin/affiliate/v2/payouts${q.toString() ? `?${q}` : ""}`);
    },
    affV2AdminApprovePayout: (id) =>
        apiFetch(`/admin/affiliate/v2/payouts/${encodeURIComponent(id)}/approve`, {
            method: "POST", body: "{}",
        }),
    affV2AdminRejectPayout: (id, reason) =>
        apiFetch(`/admin/affiliate/v2/payouts/${encodeURIComponent(id)}/reject`, {
            method: "POST", body: JSON.stringify({ reason }),
        }),
    affV2AdminForceMonthlyCalc: (month = null) =>
        apiFetch(`/admin/affiliate/v2/payouts/force-monthly-calc${month ? `?month=${month}` : ""}`, {
            method: "POST", body: "{}",
        }),
    affV2AdminReportGenerateNow: () =>
        apiFetch("/admin/affiliate/v2/report/generate-now", {
            method: "POST", body: "{}",
        }),
    affV2AdminReports: (limit = 10) =>
        apiFetch(`/admin/affiliate/v2/reports?limit=${limit}`),
    affV2AdminAuditLog: (limit = 50) =>
        apiFetch(`/admin/affiliate/v2/audit-log?limit=${limit}`),

    // ---------- Phase 16c · Marketplace v3 ----------
    // Creator (self)
    mp3CreatorDashboard: () => apiFetch("/marketplace/v3/creator/dashboard"),
    mp3CreatorAssets: (limit = 100) =>
        apiFetch(`/marketplace/v3/creator/assets?limit=${limit}`),
    mp3CreatorRevenue: (month = null) =>
        apiFetch(`/marketplace/v3/creator/revenue${month ? `?month=${month}` : ""}`),
    mp3CreatorPricingHint: (assetId) =>
        apiFetch(`/marketplace/v3/creator/pricing-hint/${encodeURIComponent(assetId)}`),
    mp3UploadAsset: (formData) =>
        fetch(`/api/marketplace/v3/assets/upload`, {
            method: "POST", credentials: "include", body: formData,
        }).then(async (r) => {
            const text = await r.text();
            let data = null;
            try { data = text ? JSON.parse(text) : null; } catch { data = { raw: text }; }
            if (!r.ok) {
                const err = new Error(
                    _formatApiErrorMessage(data, `Upload failed (${r.status})`),
                );
                err.status = r.status; err.data = data;
                throw err;
            }
            return data;
        }),
    mp3AddVersion: (assetId, formData) =>
        fetch(`/api/marketplace/v3/assets/${encodeURIComponent(assetId)}/versions`, {
            method: "POST", credentials: "include", body: formData,
        }).then(async (r) => {
            const text = await r.text();
            let data = null;
            try { data = text ? JSON.parse(text) : null; } catch { data = { raw: text }; }
            if (!r.ok) {
                const err = new Error(
                    _formatApiErrorMessage(data, `Upload failed (${r.status})`),
                );
                err.status = r.status; err.data = data;
                throw err;
            }
            return data;
        }),
    mp3ListVersions: (assetId) =>
        apiFetch(`/marketplace/v3/assets/${encodeURIComponent(assetId)}/versions`),
    mp3UpdatePrice: (assetId, price_credits) =>
        apiFetch(`/marketplace/v3/assets/${encodeURIComponent(assetId)}/price`, {
            method: "PATCH", body: JSON.stringify({ price_credits }),
        }),
    // Browse
    mp3Browse: (opts = {}) => {
        const q = new URLSearchParams();
        Object.entries(opts).forEach(([k, v]) => (v || v === 0) && q.set(k, v));
        return apiFetch(`/marketplace/v3/browse${q.toString() ? `?${q}` : ""}`);
    },
    mp3AssetDetail: (slug) =>
        apiFetch(`/marketplace/v3/assets/${encodeURIComponent(slug)}`),
    mp3PurchaseAsset: (assetId) =>
        apiFetch(`/marketplace/v3/assets/${encodeURIComponent(assetId)}/purchase`, {
            method: "POST", body: "{}",
        }),
    // Admin
    mp3AdminOverview: () => apiFetch("/admin/marketplace/v3/overview"),
    mp3AdminModeration: (status = null, limit = 50) => {
        const q = new URLSearchParams();
        if (status) q.set("status", status);
        q.set("limit", limit);
        return apiFetch(`/admin/marketplace/v3/moderation?${q}`);
    },
    mp3AdminApprove: (assetId, notes = "") =>
        apiFetch(`/admin/marketplace/v3/moderation/${encodeURIComponent(assetId)}/approve`, {
            method: "POST", body: JSON.stringify({ notes }),
        }),
    mp3AdminReject: (assetId, reason, notes = "") =>
        apiFetch(`/admin/marketplace/v3/moderation/${encodeURIComponent(assetId)}/reject`, {
            method: "POST", body: JSON.stringify({ reason, notes }),
        }),
    mp3AdminRequestChanges: (assetId, requested_changes, notes = "") =>
        apiFetch(`/admin/marketplace/v3/moderation/${encodeURIComponent(assetId)}/request-changes`, {
            method: "POST", body: JSON.stringify({ requested_changes, notes }),
        }),
    mp3AdminRollbackVersion: (assetId, version) =>
        apiFetch(`/admin/marketplace/v3/versions/${encodeURIComponent(assetId)}/${encodeURIComponent(version)}/rollback`, {
            method: "POST", body: "{}",
        }),
    mp3AdminPayouts: (opts = {}) => {
        const q = new URLSearchParams();
        Object.entries(opts).forEach(([k, v]) => (v || v === 0) && q.set(k, v));
        return apiFetch(`/admin/marketplace/v3/payouts${q.toString() ? `?${q}` : ""}`);
    },
    mp3AdminApprovePayout: (id) =>
        apiFetch(`/admin/marketplace/v3/payouts/${encodeURIComponent(id)}/approve`, {
            method: "POST", body: "{}",
        }),
    mp3AdminRejectPayout: (id, reason) =>
        apiFetch(`/admin/marketplace/v3/payouts/${encodeURIComponent(id)}/reject`, {
            method: "POST", body: JSON.stringify({ reason }),
        }),
    mp3AdminForceMonthlyCalc: (month = null) =>
        apiFetch(`/admin/marketplace/v3/payouts/force-monthly-calc${month ? `?month=${month}` : ""}`, {
            method: "POST", body: "{}",
        }),
    mp3AdminAuditLog: (limit = 50) =>
        apiFetch(`/admin/marketplace/v3/audit-log?limit=${limit}`),
    mp3AdminPopularTags: (limit = 20) =>
        apiFetch(`/admin/marketplace/v3/tags/popular?limit=${limit}`),

    // ---------- Phase 17e · Tier engine (Ambassador + Verified Creator) ----------
    tierMyStatus: () => apiFetch("/tier/my-status"),
    tierAdminHolders: (tier, limit = 100) =>
        apiFetch(`/admin/tier/holders?tier=${encodeURIComponent(tier)}&limit=${limit}`),
    tierAdminAssign: (tier, userId, reason) =>
        apiFetch(`/admin/tier/${encodeURIComponent(tier)}/assign/${encodeURIComponent(userId)}`, {
            method: "POST", body: JSON.stringify({ reason }),
        }),
    tierAdminRevoke: (tier, userId, reason) =>
        apiFetch(`/admin/tier/${encodeURIComponent(tier)}/revoke/${encodeURIComponent(userId)}`, {
            method: "POST", body: JSON.stringify({ reason }),
        }),
    tierAdminEvaluateNow: () =>
        apiFetch("/admin/tier/evaluate-now", { method: "POST", body: "{}" }),
    tierAdminAuditLog: (limit = 100, tier = null) => {
        const q = new URLSearchParams({ limit });
        if (tier) q.set("tier", tier);
        return apiFetch(`/admin/tier/audit-log?${q}`);
    },

    // ---------------------------------------------------------- //
    // Phase II · Wave A — Universe Hub (Agent Registry + Tasks + Bus) //
    // ---------------------------------------------------------- //

    // Agents (A.1)
    agentsList: (scope = "mine", limit = 200) =>
        apiFetch(`/v7/agents?scope=${encodeURIComponent(scope)}&limit=${limit}`),
    agentGet: (id) => apiFetch(`/v7/agents/${encodeURIComponent(id)}`),
    agentCreate: (body) =>
        apiFetch("/v7/agents", { method: "POST", body: JSON.stringify(body) }),
    agentUpdate: (id, body) =>
        apiFetch(`/v7/agents/${encodeURIComponent(id)}`,
            { method: "PUT", body: JSON.stringify(body) }),
    agentActivate: (id) =>
        apiFetch(`/v7/agents/${encodeURIComponent(id)}/activate`, { method: "POST", body: "{}" }),
    agentPause: (id) =>
        apiFetch(`/v7/agents/${encodeURIComponent(id)}/pause`, { method: "POST", body: "{}" }),
    agentArchive: (id) =>
        apiFetch(`/v7/agents/${encodeURIComponent(id)}/archive`, { method: "POST", body: "{}" }),
    agentInvoke: (id, body, idempKey) =>
        apiFetch(`/v7/agents/${encodeURIComponent(id)}/invoke`, {
            method: "POST",
            headers: idempKey ? { "Idempotency-Key": idempKey } : {},
            body: JSON.stringify(body),
        }),
    agentInvocationsList: (id, limit = 50) =>
        apiFetch(`/v7/agents/${encodeURIComponent(id)}/invocations?limit=${limit}`),
    agentLifecycleHistory: (id, limit = 50) =>
        apiFetch(`/v7/agents/${encodeURIComponent(id)}/lifecycle?limit=${limit}`),
    agentStats: (range = "7d") =>
        apiFetch(`/v7/agents/stats?range=${encodeURIComponent(range)}`),

    // Task graphs (A.2)
    tasksList: (scope = "mine", limit = 200) =>
        apiFetch(`/v7/agent-tasks?scope=${encodeURIComponent(scope)}&limit=${limit}`),
    taskGet: (id) => apiFetch(`/v7/agent-tasks/${encodeURIComponent(id)}`),
    taskCreate: (body) =>
        apiFetch("/v7/agent-tasks", { method: "POST", body: JSON.stringify(body) }),
    taskUpdate: (id, body) =>
        apiFetch(`/v7/agent-tasks/${encodeURIComponent(id)}`,
            { method: "PUT", body: JSON.stringify(body) }),
    taskActivate: (id) =>
        apiFetch(`/v7/agent-tasks/${encodeURIComponent(id)}/activate`, { method: "POST", body: "{}" }),
    taskArchive: (id) =>
        apiFetch(`/v7/agent-tasks/${encodeURIComponent(id)}`, { method: "DELETE" }),
    taskValidate: (graph, maxTotalCredits = 100) =>
        apiFetch("/v7/agent-tasks/validate", {
            method: "POST",
            body: JSON.stringify({ graph, max_total_credits: maxTotalCredits }),
        }),
    taskExecute: (id, input, idempKey, asyncMode = false) =>
        apiFetch(`/v7/agent-tasks/${encodeURIComponent(id)}/execute`, {
            method: "POST",
            headers: idempKey ? { "Idempotency-Key": idempKey } : {},
            body: JSON.stringify({ input: input || {}, async: !!asyncMode }),
        }),
    taskExecutionGet: (executionId) =>
        apiFetch(`/v7/agent-tasks/executions/${encodeURIComponent(executionId)}`),
    taskExecutionCancel: (executionId) =>
        apiFetch(`/v7/agent-tasks/executions/${encodeURIComponent(executionId)}/cancel`,
            { method: "POST", body: "{}" }),
    tasksStats: (range = "7d") =>
        apiFetch(`/v7/agent-tasks/stats?range=${encodeURIComponent(range)}`),

    // Messaging bus (A.2)
    msgInbox: (toAgentId, status = null, limit = 50) => {
        const q = new URLSearchParams({ to_agent_id: toAgentId, limit });
        if (status) q.set("status", status);
        return apiFetch(`/v7/agent-messages/inbox?${q}`);
    },
    msgSend: (body, idempKey) =>
        apiFetch("/v7/agent-messages", {
            method: "POST",
            headers: idempKey ? { "Idempotency-Key": idempKey } : {},
            body: JSON.stringify(body),
        }),
    msgAck: (id) =>
        apiFetch(`/v7/agent-messages/${encodeURIComponent(id)}/acknowledge`,
            { method: "POST", body: "{}" }),
    msgRetry: (id) =>
        apiFetch(`/v7/agent-messages/${encodeURIComponent(id)}/retry`,
            { method: "POST", body: "{}" }),
    msgStats: (range = "7d") =>
        apiFetch(`/v7/agent-messages/stats?range=${encodeURIComponent(range)}`),

    // -------------------------------------------------------------- //
    // Phase II · Wave A.4 · Observability (SSE stream · admin replay)  //
    // -------------------------------------------------------------- //

    /** Return the absolute URL of the SSE stream for a given execution.
     *  Client uses ``new EventSource(url, { withCredentials: true })``.
     *  ``Last-Event-ID`` reconnection is handled transparently by the
     *  browser · the server pulls persisted events from Mongo.
     */
    taskExecutionStreamUrl: (executionId) =>
        `${BACKEND_URL}/api/v7/agent-tasks/executions/${encodeURIComponent(executionId)}/stream`,

    /** Admin · replay an invocation with optional overrides.
     *  Body shape: ``{override_system_prompt?, override_model?,
     *  override_temperature?, override_max_tokens?, charge_to_user?}``.
     */
    adminInvocationReplay: (agentId, invocationId, body = {}) =>
        apiFetch(
            `/v7/agents/${encodeURIComponent(agentId)}` +
            `/invocations/${encodeURIComponent(invocationId)}/replay`,
            {
                method: "POST",
                body: JSON.stringify(body || {}),
            },
        ),
};

export { API, BACKEND_URL, NetworkError };
